import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, Col, Row } from 'antd';
import { camelCase } from 'lodash';
import { Colors } from 'works/legacy/lib/style-helper';
import {
  BaseButton,
  Currency,
  Icon,
  Tag,
  Text,
  Title,
  Gutter,
  Card,
  Link,
} from '@common/components';
import { RemoveTerm } from './remove-term';
import { EditAssignmentTermModal } from 'works/components/assignment-extension-summary/edit-assignment-term-modal';
import { buildDialogUrl, titleize } from '@common/services';
import { useCurrentMembership } from '@identity/hooks';
import { getExtensionOfferReasonDescription } from '@working-experience/services';

export const fieldHasChanges = (fieldName, term) => {
  if (!term.updatedFields) return false;
  if (term.updatedFields.length === 0) return false;

  return term.updatedFields
    .map((field) => camelCase(field))
    .includes(fieldName);
};

export function TermDetails({
  term,
  allowEditTerm,
  isExtension,
  isActiveTerm = false,
  extensionReason,
  showHistoryButton = false,
  showHistoryTerms = false,
  historyTerms,
  onHistoryButtonClick = () => {},
}) {
  const [showRemoveModal, setRemoveShowModal] = useState(false);
  const [showEditTermModal, setShowEditTermModal] = useState(false);

  const currentMembership = useCurrentMembership();

  const showAssignmentTermBillingRules = currentMembership
    ? currentMembership.canShowAssignmentTermBillingRules
    : false;

  const hasBillingConfig =
    term.staffingRequestAssignment.worksBillingConfig?.id;

  const showBillingRulesButton =
    showAssignmentTermBillingRules &&
    hasBillingConfig &&
    term.staffingRequestAssignment.organization.billingEnabled;

  const textFieldLayout = {
    secondary: !isActiveTerm,
  };

  const textValueLayout = {
    bold: isActiveTerm,
  };

  const titleLayout = {
    weight: isActiveTerm ? 'bold' : 'light',
  };

  const termTitleText = () => {
    const effectiveAt = `${moment(term.effectiveAt).format('L')} ${moment(
      term.effectiveAt
    ).format('ddd')}`;

    if (isActiveTerm) {
      return `Effective Term: ${effectiveAt}`;
    }

    if (term.past) {
      return `Past Term: ${effectiveAt}`;
    }

    if (term.upcoming) {
      return `Upcoming Term: ${effectiveAt}`;
    }

    if (term.irrelevant) {
      return 'Previous Version';
    }

    return `Term: ${effectiveAt}`;
  };

  const billingRatesDrawerUrl = buildDialogUrl('billing-rates-drawer', {
    props: { termId: term.id },
  });

  return (
    <>
      <Card>
        <article
          className={`TermSummary ${isActiveTerm ? 'is-active' : ''} ${
            term.past ? 'is-past' : ''
          }`}
        >
          <Row style={{ alignItems: 'baseline' }} gutter={[0, 12]}>
            <Col>
              <Title size="h4" {...titleLayout}>
                {termTitleText()}
              </Title>
            </Col>
            {!term.irrelevant && allowEditTerm && (
              <Col>
                <Button type="link" onClick={() => setShowEditTermModal(true)}>
                  <Icon icon="pencil" color="elm" size="lg" />
                </Button>
                <EditAssignmentTermModal
                  showModal={showEditTermModal}
                  onClose={() => setShowEditTermModal(false)}
                  assignmentTermId={term.id}
                  isExtension={isExtension}
                />
              </Col>
            )}
          </Row>

          <Row gutter={[0, 24]}>
            <Col span={12}>
              <Row gutter={[0, 4]}>
                <Col span={8}>
                  <Text {...textFieldLayout}>Unit:</Text>
                </Col>
                <Col>
                  <Text {...textValueLayout}>{term.organizationUnit.name}</Text>
                </Col>
              </Row>
              <Row gutter={[0, 4]}>
                <Col span={8}>
                  <Text {...textFieldLayout}>Shift Type:</Text>
                </Col>
                <Col>
                  <Text {...textValueLayout}>{term.shiftType}</Text>
                </Col>
              </Row>
              <Row gutter={[0, 4]}>
                <Col span={8}>
                  <Text {...textFieldLayout}>Shifts Per Week:</Text>
                </Col>
                <Col>
                  <Text {...textValueLayout}>{term.shiftsPerWeek}</Text>
                </Col>
              </Row>
              <Row gutter={[0, 4]}>
                <Col span={8}>
                  <Text {...textFieldLayout}>Hours Per Shift:</Text>
                </Col>
                <Col>
                  <Text {...textValueLayout}>{term.hoursPerShift}</Text>
                </Col>
              </Row>
              {!!term.overtimeThreshold && (
                <Row>
                  <Col span={8}>
                    <Text {...textFieldLayout}>Overtime Threshold:</Text>
                  </Col>
                  <Col>
                    <Text {...textValueLayout}>
                      {term.overtimeThreshold} hours
                    </Text>
                  </Col>
                </Row>
              )}
            </Col>

            <Col span={8}>
              <Row gutter={[0, 4]}>
                <Col span={8}>
                  <Text {...textFieldLayout}>Regular Rate (hr):</Text>
                </Col>
                <Col>
                  <Row gutter={[0, 4]}>
                    <Gutter direction="right">
                      <Text {...textValueLayout}>
                        <Currency value={term.billingRegularRate} />
                      </Text>
                    </Gutter>
                    <Tag color="steelLighter">
                      {titleize(term.employmentType)}
                    </Tag>
                  </Row>
                </Col>
              </Row>
              <Row gutter={[0, 4]}>
                <Col span={8}>
                  <Text {...textFieldLayout}>Time Off:</Text>
                </Col>
                <Col>
                  <Text {...textValueLayout}>
                    {term.timeoffDescription || 'N/A'}
                  </Text>
                </Col>
              </Row>
              <Row gutter={[0, 4]}>
                <Col span={8}>
                  <Text {...textFieldLayout}>Hiring Leader:</Text>
                </Col>
                <Col>
                  <Text className="sentry-mask" {...textValueLayout}>
                    {term.hiringLeader.name}
                  </Text>
                </Col>
              </Row>
              {isExtension && extensionReason && (
                <Row gutter={[0, 4]}>
                  <Col span={8}>
                    <Text {...textFieldLayout}>Reason:</Text>
                  </Col>
                  <Col>
                    <Text {...textValueLayout}>
                      {extensionReason
                        ? getExtensionOfferReasonDescription(extensionReason)
                        : 'N/A'}
                    </Text>
                  </Col>
                </Row>
              )}
            </Col>

            <Col span={4}>
              <Text align="right">
                {showBillingRulesButton && (
                  <Link to={billingRatesDrawerUrl}>
                    <BaseButton className="billingRatesButton">
                      Billing Rates
                    </BaseButton>
                  </Link>
                )}
              </Text>
            </Col>
          </Row>

          {term.createdByName && (
            <Gutter direction="top" size="md">
              <Text className="sentry-mask" {...textFieldLayout}>
                {term.createdByName} made these changes on{' '}
                {moment(term.createdAt).format('L')}.
              </Text>
            </Gutter>
          )}
          <Gutter direction="top">
            <Row>
              {showHistoryButton && !term.irrelevant && (
                <Col>
                  <Button
                    style={{ marginRight: 15, padding: 0 }}
                    onClick={onHistoryButtonClick}
                    type="link"
                  >
                    View changes
                    <Gutter direction="left" fit>
                      <Icon
                        icon={showHistoryTerms ? 'chevronUp' : 'chevronDown'}
                        kind="regular"
                        size="md"
                      />
                    </Gutter>
                  </Button>
                </Col>
              )}
              {term.canVoid.value && (
                <Col>
                  <Button
                    type="link"
                    style={{ paddingLeft: 0 }}
                    onClick={() => setRemoveShowModal(true)}
                  >
                    <Text underline color="coral" bold>
                      Remove Term
                    </Text>
                  </Button>
                  <RemoveTerm
                    assignmentTermId={term.id}
                    showModal={showRemoveModal}
                    onClose={() => setRemoveShowModal(false)}
                  />
                </Col>
              )}
            </Row>
          </Gutter>

          {showHistoryTerms &&
            historyTerms.map((term) => {
              return (
                <section key={term.id} className="TermSummaryHistory">
                  <TermDetails
                    term={term}
                    isExtension={isExtension}
                    extensionReason={extensionReason}
                  />
                </section>
              );
            })}
        </article>
      </Card>
      <style jsx>{`
        .TermSummaryHistory {
          margin: 24px 0;
        }
        .TermSummaryHistory:last-of-type {
          margin: 24px 0 0 0;
        }
        .TermSummary:not(:last-of-type) {
          border-bottom: 1px solid ${Colors.steel};
        }
        .TermSummary.is-active {
          border-radius: 4px;
          background-color: ${Colors.grassLightest};
          border: 0;
          padding: 16px;
        }
        :global(.TermSummary .billingRatesButton) {
          margin-top: -5px;
          margin-left: 12px;
        }
        :global(.TermSummary.is-past .ant-typography):not(
          .ant-typography-danger
        ) {
          color: ${Colors.midnightLighter};
        }
        .TermSummary.is-past {
          background-color: ${Colors.steelLightest};
        }
      `}</style>
    </>
  );
}

TermDetails.propTypes = {
  term: PropTypes.shape({
    id: PropTypes.string.isRequired,
    effectiveAt: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
    lengthInWeeks: PropTypes.number.isRequired,
    billingRegularRate: PropTypes.number.isRequired,
    shiftType: PropTypes.string.isRequired,
    shiftsPerWeek: PropTypes.number.isRequired,
    hoursPerShift: PropTypes.number.isRequired,
    estimatedBudget: PropTypes.number.isRequired,
    timeoffDescription: PropTypes.string,
    employmentType: PropTypes.string,
    organizationUnit: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    staffingRequestAssignment: PropTypes.shape({
      id: PropTypes.string.isRequired,
      employmentType: PropTypes.string,
      organization: PropTypes.shape({
        id: PropTypes.string.isRequired,
        billingEnabled: PropTypes.bool.isRequired,
      }).isRequired,
      worksBillingConfig: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    }).isRequired,
    hiringLeader: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    extensionReason: PropTypes.string,
    createdByName: PropTypes.string,
    irrelevant: PropTypes.bool.isRequired,
    past: PropTypes.bool.isRequired,
    upcoming: PropTypes.bool.isRequired,
    updatedFields: PropTypes.arrayOf(PropTypes.string),
    createdAt: PropTypes.string,
    canVoid: PropTypes.shape({ value: PropTypes.bool.isRequired }),
    overtimeThreshold: PropTypes.number,
  }).isRequired,
  historyTerms: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    })
  ),
  isExtension: PropTypes.bool,
  isActiveTerm: PropTypes.bool,
  extensionReason: PropTypes.string,
  showHistoryButton: PropTypes.bool,
  showHistoryTerms: PropTypes.bool,
  onHistoryButtonClick: PropTypes.func,
  allowEditTerm: PropTypes.bool,
};
